import React, { useState, useEffect } from 'react';
import {
  Package,
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { productsApi, categoriesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Products = () => {
  const { isAdmin, isSales } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    cas_number: '',
    purity: '',
    price: '',
    moq: '1',
    unit: 'kg',
    hazard_class: 'none',
    stock_quantity: '0',
    reorder_level: '10',
    lead_time_days: '7',
    packaging_options: '',
    sds_url: '',
    image_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        productsApi.getAll({ active_only: false }),
        categoriesApi.getAll()
      ]);
      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        moq: parseInt(formData.moq),
        stock_quantity: parseInt(formData.stock_quantity),
        reorder_level: parseInt(formData.reorder_level),
        lead_time_days: parseInt(formData.lead_time_days),
        packaging_options: formData.packaging_options.split(',').map(s => s.trim()).filter(Boolean)
      };

      if (editingProduct) {
        await productsApi.update(editingProduct.id, payload);
        toast.success('Product updated successfully');
      } else {
        await productsApi.create(payload);
        toast.success('Product created successfully');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsApi.delete(id);
      toast.success('Product deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category_id: product.category_id,
      cas_number: product.cas_number || '',
      purity: product.purity || '',
      price: product.price.toString(),
      moq: product.moq.toString(),
      unit: product.unit,
      hazard_class: product.hazard_class,
      stock_quantity: product.stock_quantity.toString(),
      reorder_level: product.reorder_level.toString(),
      lead_time_days: product.lead_time_days.toString(),
      packaging_options: (product.packaging_options || []).join(', '),
      sds_url: product.sds_url || '',
      image_url: product.image_url || '',
      is_active: product.is_active
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      category_id: '',
      cas_number: '',
      purity: '',
      price: '',
      moq: '1',
      unit: 'kg',
      hazard_class: 'none',
      stock_quantity: '0',
      reorder_level: '10',
      lead_time_days: '7',
      packaging_options: '',
      sds_url: '',
      image_url: '',
      is_active: true
    });
  };

  const getHazardBadgeClass = (hazardClass) => {
    const classes = {
      flammable: 'hazard-flammable',
      corrosive: 'hazard-corrosive',
      toxic: 'hazard-toxic',
      oxidizer: 'hazard-oxidizer',
      irritant: 'hazard-irritant',
      none: 'hazard-none',
    };
    return classes[hazardClass] || 'hazard-none';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.cas_number && product.cas_number.includes(searchTerm));
    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="products-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-64 rounded-md"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Products</h1>
          <p className="text-slate-600 mt-1">{products.length} products in catalog</p>
        </div>
        {(isAdmin || isSales) && (
          <Button 
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            data-testid="add-product-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or CAS number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="product-search"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="empty-state py-16">
            <Package className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No products found</h3>
            <p className="text-slate-500">Try adjusting your search or filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="card-hover" data-testid={`product-card-${product.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{product.name}</h3>
                      {!product.is_active && (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                    {product.category_name && (
                      <p className="text-xs text-slate-500">{product.category_name}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setViewProduct(product)}>
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      {(isAdmin || isSales) && (
                        <>
                          <DropdownMenuItem onClick={() => openEditModal(product)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{product.description}</p>

                <div className="space-y-2 mb-4 text-sm">
                  {product.cas_number && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">CAS</span>
                      <span className="font-mono text-slate-700">{product.cas_number}</span>
                    </div>
                  )}
                  {product.purity && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Purity</span>
                      <span className="font-mono text-slate-700">{product.purity}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">MOQ</span>
                    <span className="font-mono text-slate-700">{product.moq} {product.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Stock</span>
                    <span className={`font-mono ${product.stock_quantity <= product.reorder_level ? 'text-red-600' : 'text-slate-700'}`}>
                      {product.stock_quantity} {product.unit}
                      {product.stock_quantity <= product.reorder_level && (
                        <AlertTriangle className="w-3 h-3 inline ml-1 text-amber-500" />
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <Badge className={`${getHazardBadgeClass(product.hazard_class)} capitalize text-xs`}>
                    {product.hazard_class === 'none' ? 'Safe' : product.hazard_class}
                  </Badge>
                  <div className="text-right">
                    <span className="text-lg font-bold text-slate-900">${product.price.toFixed(2)}</span>
                    <span className="text-sm text-slate-500">/{product.unit}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Product Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label className="form-label">Product Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label className="form-label">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                />
              </div>

              <div>
                <Label className="form-label">Category</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Hazard Class</Label>
                <Select 
                  value={formData.hazard_class} 
                  onValueChange={(value) => setFormData({ ...formData, hazard_class: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Safe)</SelectItem>
                    <SelectItem value="flammable">Flammable</SelectItem>
                    <SelectItem value="corrosive">Corrosive</SelectItem>
                    <SelectItem value="toxic">Toxic</SelectItem>
                    <SelectItem value="oxidizer">Oxidizer</SelectItem>
                    <SelectItem value="irritant">Irritant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">CAS Number</Label>
                <Input
                  value={formData.cas_number}
                  onChange={(e) => setFormData({ ...formData, cas_number: e.target.value })}
                  placeholder="e.g., 67-64-1"
                />
              </div>

              <div>
                <Label className="form-label">Purity</Label>
                <Input
                  value={formData.purity}
                  onChange={(e) => setFormData({ ...formData, purity: e.target.value })}
                  placeholder="e.g., 99.5%"
                />
              </div>

              <div>
                <Label className="form-label">Price per Unit ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label className="form-label">Unit</Label>
                <Select 
                  value={formData.unit} 
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                    <SelectItem value="mL">mL</SelectItem>
                    <SelectItem value="unit">unit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">MOQ (Min Order Qty)</Label>
                <Input
                  type="number"
                  value={formData.moq}
                  onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label className="form-label">Stock Quantity</Label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label className="form-label">Reorder Level</Label>
                <Input
                  type="number"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                />
              </div>

              <div>
                <Label className="form-label">Lead Time (Days)</Label>
                <Input
                  type="number"
                  value={formData.lead_time_days}
                  onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                />
              </div>

              <div className="col-span-2">
                <Label className="form-label">Packaging Options (comma-separated)</Label>
                <Input
                  value={formData.packaging_options}
                  onChange={(e) => setFormData({ ...formData, packaging_options: e.target.value })}
                  placeholder="e.g., 25kg drum, 200L barrel, IBC"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="save-product-button">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Product Modal */}
      <Dialog open={!!viewProduct} onOpenChange={() => setViewProduct(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewProduct?.name}</DialogTitle>
          </DialogHeader>
          {viewProduct && (
            <div className="space-y-4 py-4">
              <p className="text-slate-600">{viewProduct.description}</p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Category</span>
                  <p className="font-medium">{viewProduct.category_name || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">CAS Number</span>
                  <p className="font-mono">{viewProduct.cas_number || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Purity</span>
                  <p className="font-mono">{viewProduct.purity || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Hazard Class</span>
                  <Badge className={`${getHazardBadgeClass(viewProduct.hazard_class)} capitalize`}>
                    {viewProduct.hazard_class}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500">Price</span>
                  <p className="font-bold">${viewProduct.price.toFixed(2)}/{viewProduct.unit}</p>
                </div>
                <div>
                  <span className="text-slate-500">MOQ</span>
                  <p className="font-mono">{viewProduct.moq} {viewProduct.unit}</p>
                </div>
                <div>
                  <span className="text-slate-500">Stock</span>
                  <p className="font-mono">{viewProduct.stock_quantity} {viewProduct.unit}</p>
                </div>
                <div>
                  <span className="text-slate-500">Lead Time</span>
                  <p>{viewProduct.lead_time_days} days</p>
                </div>
              </div>

              {viewProduct.packaging_options?.length > 0 && (
                <div>
                  <span className="text-slate-500 text-sm">Packaging Options</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {viewProduct.packaging_options.map((opt, i) => (
                      <Badge key={i} variant="secondary">{opt}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
